import { useEffect } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import {
  GlobalOutlined,
  LockOutlined,
  MobileOutlined,
  SafetyOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/features/auth/store/authStore';
// supabase import removed - not needed for sessions
import { useUpdateUserPreferences, useUserPreferences } from '../hooks/useClientData';

const { Title, Paragraph, Text } = Typography;

interface SettingsFormValues {
  language: string;
  timezone: string;
  date_format: string;
  currency: string;
}

export default function ClientSettingsPage() {
  const { user } = useAuthStore();
  const userId = user?.id ?? null;
  const [form] = Form.useForm<SettingsFormValues>();

  const { data: preferences } = useUserPreferences(userId);
  const updatePreferences = useUpdateUserPreferences();

  useEffect(() => {
    if (preferences) {
      form.setFieldsValue({
        language: preferences.language ?? 'pt-BR',
        timezone: preferences.timezone ?? 'America/Sao_Paulo',
        date_format: preferences.date_format ?? 'DD/MM/YYYY',
        currency: preferences.currency ?? 'BRL',
      });
    }
  }, [preferences, form]);

  const handleSettingsSubmit = async () => {
    if (!userId) return;
    try {
      const values = await form.validateFields();
      await updatePreferences.mutateAsync({ userId, values });
    } catch (error: any) {
      message.error(error?.message || 'Não foi possível salvar');
    }
  };

  // Sessões ocultadas (implementação futura)

  return (
    <div className="space-y-6">
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={3} style={{ marginBottom: 0, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Configurações
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Ajuste idioma, fuso horário e revise a segurança da sua conta.
          </Paragraph>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Preferências gerais" extra={<GlobalOutlined />}>
            <Form layout="vertical" form={form} onFinish={handleSettingsSubmit}>
              <Form.Item label="Idioma" name="language">
                <Select
                  options={[
                    { value: 'pt-BR', label: 'Português (Brasil)' },
                    { value: 'en-US', label: 'Inglês (EUA)' },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Fuso horário" name="timezone">
                <Select
                  showSearch
                  options={[
                    { value: 'America/Sao_Paulo', label: 'GMT-3 Brasília' },
                    { value: 'America/Mexico_City', label: 'GMT-6 Cidade do México' },
                    { value: 'America/Montevideo', label: 'GMT-3 Montevidéu' },
                  ]}
                />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Formato de data" name="date_format">
                    <Input placeholder="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Moeda preferencial" name="currency">
                    <Select
                      options={[
                        { value: 'BRL', label: 'Real Brasileiro (BRL)' },
                        { value: 'USD', label: 'Dólar Americano (USD)' },
                        { value: 'EUR', label: 'Euro (EUR)' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Space>
                <Button type="primary" htmlType="submit" loading={updatePreferences.isPending}>
                  Salvar configurações
                </Button>
                <Button onClick={() => form.resetFields()}>Restaurar padrão</Button>
              </Space>
            </Form>
          </Card>

          <Card title="Notificações" style={{ marginTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div className="flex items-center justify-between">
                <div>
                  <Text strong>Alertas por e-mail</Text>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Receba atualizações sobre execuções e faturamento
                  </Paragraph>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Text strong>Alertas críticos por SMS</Text>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Notifique incidentes de produção imediatamente
                  </Paragraph>
                </div>
                <Switch />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Segurança" extra={<SafetyOutlined />}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div className="flex items-center justify-between">
                <div>
                  <Text strong>Autenticação em duas etapas</Text>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Garanta mais segurança ativando OTP via app autenticador
                  </Paragraph>
                </div>
                <Button icon={<LockOutlined />}>Ativar 2FA</Button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Text strong>WhatsApp corporativo</Text>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Receba alertas críticos diretamente no seu número principal
                  </Paragraph>
                </div>
                <Button icon={<MobileOutlined />}>Cadastrar número</Button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Text strong>Alertas de atividade suspeita</Text>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Monitore logins em locais não reconhecidos
                  </Paragraph>
                </div>
                <Button icon={<WarningOutlined />}>Configurar alertas</Button>
              </div>
            </Space>
          </Card>

          {/* Sessões ativas ocultadas — recurso futuro */}
        </Col>
      </Row>
    </div>
  );
}
